import { Box, Grid, Skeleton, Stack, Tab } from "@mui/material";
import { FilterTabs } from "@zfp-akademie-a-s/zfp-design";
import { BlockedIcon, CheckCircleIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { DashboardChart } from "Components/Dashboard/DashboardChart";
import { MetricItem } from "Components/Dashboard/MetricItem";
import { InProgressIcon } from "Components/_Shared/Icons/Icons";
import { AuthenticatedLayout } from "Components/_Shared/Layouts/AuthenticatedLayout";
import { useGetDashboardGraph, useGetDashboardSummary } from "Infrastructure/ApiZfp/AnalyzerApi";
import { GetDashboardGraphInterval } from "Infrastructure/ApiZfp/Schema";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useState } from "react";

export const DashboardPage: React.FunctionComponent = () => {
  const { t } = useResource();
  const [interval, setInterval] = useState<GetDashboardGraphInterval>(GetDashboardGraphInterval.all);
  const { user } = useUserContext();

  const { data: summaryData, isPending: isSummaryPending } = useGetDashboardSummary();
  const { data: graphData } = useGetDashboardGraph({ interval });

  const handleChange = (_: React.SyntheticEvent, index: GetDashboardGraphInterval) => {
    setInterval(index);
  };

  return (
    <AuthenticatedLayout
      title={t(Resources.Dashboard.Title)}
      header={{
        title: (
          <>
            {t(Resources.Dashboard.Subtitle)}, {user?.firstName} {user?.lastName}
          </>
        ),
        subtitle: t(Resources.Dashboard.ActivityOverview),
      }}
    >
      <Stack spacing={Spacing.XXXL}>
        <Grid container spacing={Spacing.XXL}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <MetricItem
              metricName={t(Resources.AnalysesListing.Status.InProgress)}
              metricValue={summaryData?.inProgress.count || 0}
              changeInPercent={summaryData?.inProgress.monthlyChange || 0}
              loading={isSummaryPending}
              icon={<InProgressIcon />}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <MetricItem
              metricName={t(Resources.AnalysesListing.Status.Completed)}
              metricValue={summaryData?.completed.count || 0}
              changeInPercent={summaryData?.completed.monthlyChange || 0}
              loading={isSummaryPending}
              icon={<CheckCircleIcon />}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <MetricItem
              metricName={t(Resources.AnalysesListing.Status.Declined)}
              metricValue={summaryData?.declined.count || 0}
              changeInPercent={summaryData?.declined.monthlyChange || 0}
              loading={isSummaryPending}
              icon={<BlockedIcon />}
            />
          </Grid>
        </Grid>
        {!!graphData ? (
          <DashboardChart
            data={graphData}
            interval={interval}
            renderControls={() => {
              return (
                <Box marginBottom={Spacing.M}>
                  <FilterTabs value={interval} onChange={handleChange}>
                    <Tab label={t(Resources.Dashboard.GraphInterval.AllTime)} value={GetDashboardGraphInterval.all} />
                    <Tab label={t(Resources.Dashboard.GraphInterval.TwelveMonths)} value={GetDashboardGraphInterval.year} />
                    <Tab label={t(Resources.Dashboard.GraphInterval.SixMonths)} value={GetDashboardGraphInterval.half_year} />
                    <Tab label={t(Resources.Dashboard.GraphInterval.ThirtyDays)} value={GetDashboardGraphInterval.month} />
                    <Tab label={t(Resources.Dashboard.GraphInterval.SevenDays)} value={GetDashboardGraphInterval.week} />
                  </FilterTabs>
                </Box>
              );
            }}
          />
        ) : (
          <Skeleton height={280} />
        )}
      </Stack>
    </AuthenticatedLayout>
  );
};
